import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Sparkles } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const InvitationBuilder = ({ eventId, event, onSuccess }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [hasInvitation, setHasInvitation] = useState(false);
  const [formData, setFormData] = useState({
    theme: 'frozen',
    person_names: '',
    event_type: 'Birthday',
    event_title: '',
    date_from: '',
    date_to: '',
    time_from: '',
    time_to: '',
    venue: '',
    photo_url: '',
    organizers: '',
    background_image_url: ''
  });
  const [uploadingBg, setUploadingBg] = useState(false);

  useEffect(() => {
    checkExistingInvitation();
  }, [eventId]);

  const checkExistingInvitation = async () => {
    try {
      await axios.get(`${API}/events/${eventId}/invitation`);
      setHasInvitation(true);
    } catch (error) {
      setHasInvitation(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.person_names || !formData.event_title || !formData.date_from || !formData.time_from || !formData.venue) {
      toast.error('Please fill all required fields');
      return;
    }

    try {
      await axios.post(`${API}/events/${eventId}/invitation`, formData, { withCredentials: true });
      toast.success('Invitation created successfully!');
      setDialogOpen(false);
      setHasInvitation(true);
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error('Error creating invitation:', error);
      toast.error('Failed to create invitation');
    }
  };

  const handleBackgroundUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size should be less than 5MB');
      return;
    }

    // Validate image dimensions
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    
    img.onload = async () => {
      URL.revokeObjectURL(objectUrl);
      
      const width = img.width;
      const height = img.height;
      
      // Recommended: 1600x1200 or larger (4:3 ratio)
      // Minimum acceptable: 1024x768
      // Reject: below 800x600
      
      if (width < 800 || height < 600) {
        toast.error(`Image too small! Minimum size: 800x600 pixels. Your image: ${width}x${height}px`);
        return;
      }
      
      if (width < 1024 || height < 768) {
        toast.warning(`Image resolution is low (${width}x${height}px). Recommended: 1600x1200 or larger. We'll adjust it, but quality may be reduced.`);
      }
      
      // Upload to backend
      setUploadingBg(true);
      const formData = new FormData();
      formData.append('file', file);
      
      try {
        const response = await axios.post(`${API}/upload-image`, formData, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        setFormData(prev => ({ ...prev, background_image_url: response.data.url }));
        toast.success('Background image uploaded successfully!');
      } catch (error) {
        console.error('Upload error:', error);
        toast.error('Failed to upload image');
      } finally {
        setUploadingBg(false);
      }
    };
    
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      toast.error('Failed to load image');
    };
    
    img.src = objectUrl;
  };

  const openBuilder = async () => {
    // If invitation exists, fetch and pre-fill
    if (hasInvitation) {
      try {
        const response = await axios.get(`${API}/events/${eventId}/invitation`);
        const invData = response.data;
        setFormData({
          theme: invData.theme || 'frozen',
          person_names: invData.person_names || '',
          event_type: invData.event_type || 'Birthday',
          event_title: invData.event_title || '',
          date_from: invData.date_from || '',
          date_to: invData.date_to || '',
          time_from: invData.time_from || '',
          time_to: invData.time_to || '',
          venue: invData.venue || '',
          photo_url: invData.photo_url || '',
          organizers: invData.organizers || '',
          background_image_url: invData.background_image_url || ''
        });
      } catch (error) {
        console.error('Error fetching invitation:', error);
        toast.error('Failed to load invitation');
      }
    } else {
      // Pre-fill with event data for new invitation
      setFormData({
        ...formData,
        date_from: event?.event_date || '',
        time_from: event?.event_time || ''
      });
    }
    setDialogOpen(true);
  };

  return (
    <>
      <Button
        onClick={openBuilder}
        className="gap-2 bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600"
        data-testid="create-invitation-button"
      >
        <Sparkles className="w-4 h-4" />
        {hasInvitation ? 'Update Invitation' : 'Create Invitation'}
      </Button>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{hasInvitation ? 'Update Invitation' : 'Create Invitation'} 🎉</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="person_names">Birthday Kid's Name(s) *</Label>
                <Input
                  id="person_names"
                  placeholder="e.g., Emma & Sophia"
                  value={formData.person_names}
                  onChange={(e) => setFormData({ ...formData, person_names: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="event_type">Event Type *</Label>
                <select
                  id="event_type"
                  value={formData.event_type}
                  onChange={(e) => setFormData({ ...formData, event_type: e.target.value })}
                  className="w-full p-2 border rounded-md"
                  required
                >
                  <option>Birthday</option>
                  <option>Engagement</option>
                  <option>Baby Shower</option>
                  <option>Wedding</option>
                  <option>Housewarming</option>
                  <option>Anniversary</option>
                  <option>Graduation</option>
                </select>
              </div>
            </div>

            <div>
              <Label htmlFor="event_title">Event Title *</Label>
              <Input
                id="event_title"
                placeholder="e.g., Join us for a Magical Frozen Birthday!"
                value={formData.event_title}
                onChange={(e) => setFormData({ ...formData, event_title: e.target.value })}
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="date_from">Date From *</Label>
                <Input
                  id="date_from"
                  type="date"
                  value={formData.date_from}
                  onChange={(e) => setFormData({ ...formData, date_from: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="date_to">Date To (Optional)</Label>
                <Input
                  id="date_to"
                  type="date"
                  value={formData.date_to}
                  onChange={(e) => setFormData({ ...formData, date_to: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="time_from">Time From *</Label>
                <Input
                  id="time_from"
                  type="time"
                  value={formData.time_from}
                  onChange={(e) => setFormData({ ...formData, time_from: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="time_to">Time To (Optional)</Label>
                <Input
                  id="time_to"
                  type="time"
                  value={formData.time_to}
                  onChange={(e) => setFormData({ ...formData, time_to: e.target.value })}
                />
              </div>
            </div>

            <div>
              <Label htmlFor="venue">Venue *</Label>
              <textarea
                id="venue"
                placeholder="e.g., Magical Kingdom Party Hall, 123 Ice Castle Street"
                value={formData.venue}
                onChange={(e) => setFormData({ ...formData, venue: e.target.value })}
                className="w-full p-2 border rounded-md min-h-[80px]"
                required
              />
            </div>

            <div>
              <Label htmlFor="organizers">Organized By (Optional)</Label>
              <Input
                id="organizers"
                placeholder="e.g., Parents: John & Jane Doe"
                value={formData.organizers}
                onChange={(e) => setFormData({ ...formData, organizers: e.target.value })}
              />
            </div>

            <div>
              <Label htmlFor="photo_url">Photo URL (Optional)</Label>
              <Input
                id="photo_url"
                placeholder="https://..."
                value={formData.photo_url}
                onChange={(e) => setFormData({ ...formData, photo_url: e.target.value })}
              />
              <p className="text-xs text-gray-500 mt-1">Add a photo of the birthday kid!</p>
            </div>

            <div className="border-t pt-4">
              <Label htmlFor="background_image">Custom Background Image (Optional)</Label>
              <div className="mt-2">
                <Input
                  id="background_image"
                  type="file"
                  accept="image/*"
                  onChange={handleBackgroundUpload}
                  disabled={uploadingBg}
                  className="cursor-pointer"
                />
                <div className="mt-2 space-y-1">
                  <p className="text-xs text-gray-600">
                    <strong>Recommended:</strong> 1600 x 1200 pixels or larger (4:3 ratio)
                  </p>
                  <p className="text-xs text-gray-600">
                    <strong>Minimum:</strong> 1024 x 768 pixels
                  </p>
                  <p className="text-xs text-red-500">
                    <strong>Not accepted:</strong> Below 800 x 600 pixels
                  </p>
                  {uploadingBg && <p className="text-xs text-blue-500">⏳ Uploading...</p>}
                  {formData.background_image_url && (
                    <p className="text-xs text-green-600">✅ Background image uploaded!</p>
                  )}
                </div>
              </div>
            </div>

            <Button type="submit" className="w-full" style={{background: 'linear-gradient(135deg, #a855f7 0%, #3b82f6 100%)'}}>
              {hasInvitation ? '✏️ Update Invitation' : '✨ Create Invitation'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default InvitationBuilder;